import { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getProductState } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { useCurrentLanguage } from "@store/Hooks/Context";
import { dispatchAddToCartEventCartLine } from "@store/Services/AnalyticsService";
import loadQuoteToCart from "@store/Store/Data/QuoteToCart/Handlers/LoadQuoteToCart";
import loadQuoteToQuickOrder from "@store/Store/Data/QuoteToQuickOrder/Handlers/LoadQuoteToQuickOrder";
import StoreApplicationState from "@store/Store/StoreApplicationState";
import {
    contentSpacingStyles,
    formFieldButtonIconClickableStyles,
    formFieldButtonIconStyles,
} from "@store/theme/common/mixins";
import getWidgetComponent from "@store/utilities/getWidgetComponent";
import { InfosBoxType } from "@store/Widgets/InfosBox";
import * as React from "react";
import { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const InfosBox = getWidgetComponent("InfosBox");

const enum fields {
    quickOrderContext = "quickOrderContext",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.quickOrderContext]: boolean;
    };
}

const mapStateToProps = (state: StoreApplicationState) => {
    const stateContext = state.store.data.quoteToCart;

    return {
        cart: getCurrentCartState(state).value,
        isLoading: stateContext.isLoading,
    };
};
const mapDispatchToProps = {
    loadQuoteToCart,
    loadQuoteToQuickOrder,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ImportQuoteStyles {
    form?: InjectableCss;
    importQuoteTextField?: TextFieldPresentationProps;
}

const styles = {
    importQuoteTextField: {
        iconProps: {
            src: "ChevronsRight",
            css: css`
                ${formFieldButtonIconStyles()}
            `,
        },
        iconClickableProps: {
            css: css`
                ${formFieldButtonIconClickableStyles({ variant: "secondary" })}
            `,
        },
    },
    form: {
        css: css`
            ${contentSpacingStyles(null, "1.25em")}
        `,
    },
};
export const importQuoteStyles = styles;
const StyledForm = getStyledWrapper("form");

const ImportQuote: React.FunctionComponent<Props> = ({
    loadQuoteToCart,
    loadQuoteToQuickOrder,
    isLoading,
    fields,
    cart,
}) => {
    const [quoteNumber, setQuoteNumber] = useState("");
    const [quoteErrorMessage, setQuoteErrorMessage] = useState("");
    const [isWaitingForCart, setIsWaitingForCart] = useState(false);
	const [isAddedToQuickOrder, setIsAddedToQuickOrder] = useState(false);
    const toasterContext = React.useContext(ToasterContext);
    const quoteIdRequiredMessage = translate("Quote number is required");
    const quoteIdInvalidMessage = translate("Quote number is invalid");
    const currentLanguage = useCurrentLanguage();

    React.useEffect(() => {
        if (isWaitingForCart && cart) {
            setIsWaitingForCart(false);
            cart?.cartLines?.forEach(cartLine => {
                dispatchAddToCartEventCartLine(currentLanguage, cartLine);
            });
        }
    }, [cart]);

    const handleQuoteIdChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setQuoteNumber(event.currentTarget.value);
        setQuoteErrorMessage("");
    };

    const handleSubmit = (event: React.MouseEvent<HTMLFormElement, MouseEvent>) => {
        event.preventDefault();

        if (!quoteNumber) {
            setQuoteErrorMessage(quoteIdRequiredMessage);
            return;
        }

        const regexpAlphaNumeric = new RegExp("^[a-zA-Z0-9_]*$");
        if (!regexpAlphaNumeric.test(quoteNumber)) {
            setQuoteErrorMessage(quoteIdInvalidMessage);
            return;
        }

        if (fields.quickOrderContext) {
			setIsAddedToQuickOrder(true);
            loadQuoteToQuickOrder({
                apiParameter: { quoteId: quoteNumber },
                onSegmentComplete: (step, nbComplete, nbProcessed, nbTotal) => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage(step, `${nbProcessed}`, `${nbComplete}`)}</Typography>,
                        messageType: "success",
                    });
                },
                onSuccess: (result) => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage("ImportQuote_ProductsImportedComplete", `${result.nbAdded}`, `${result.nbTotal}`)}</Typography>,
                        messageType: "success",
                    });
                },
                onError: () => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage("QuickOrder_quoteToQuickOrderNoProducts")}</Typography>,
                        messageType: "warning",
                    });
                },
            });
        } else {
            loadQuoteToCart({
                apiParameter: { quoteId: quoteNumber },
                onSegmentComplete: (nbComplete, nbTotal) => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage("ImportQuote_AddingToCart", `${nbComplete}`, `${nbTotal}`)}</Typography>,
                        messageType: "success",
                    });
                },
                onSuccess: result => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage("Cart_AllProductsAddedToCart")}</Typography>,
                        messageType: "success",
                    });
                    setIsWaitingForCart(true);
                },
                onError: () => {
                    toasterContext.addToast({
                        body: <Typography>{siteMessage("Cart_quoteToCartNoProducts")}</Typography>,
                        messageType: "warning",
                    });
                },
            });
        }
    };

    const iconClickableProps = {
        type: "submit",
        onClick: () => {},
        ...styles.importQuoteTextField?.iconClickableProps,
    };

    return (
        <LoadingOverlay loading={isLoading}>
            <StyledForm {...styles.form} onSubmit={handleSubmit}>
                <TextField
                    {...styles.importQuoteTextField}
                    iconClickableProps={iconClickableProps}
                    clickableText={translate("Import")}
                    label={translate("Quote Number:")}
                    onChange={handleQuoteIdChange}
                    error={quoteErrorMessage}
                    value={quoteNumber}
                    data-test-selector="importQuoteTextField"
                />
            </StyledForm>
			{isAddedToQuickOrder && (
				<InfosBox type={InfosBoxType.Warning} content={siteMessage("QuickOrder_reversedOrderWarning")} />
            )}
        </LoadingOverlay>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ImportQuote),
    definition: {
        group: "Common",
        displayName: "Import Quote",
        icon: "List",
        fieldDefinitions: [
            {
                name: fields.quickOrderContext,
                displayName: "Quick Order Context",
                tooltip:
                    "Determines whether the items from the quote should be added to the Quick Order. If unchecked, the items will be added to the cart.",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
